import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, Plus, Globe } from "lucide-react";
import { AnimatedThemeToggler } from "@/components/ui/animated-theme-toggler";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { TicketHallLogo } from "@/components/TicketHallLogo";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { cn } from "@/lib/utils";
import { NotificationBell } from "@/components/NotificationBell";
import { useTranslation } from "react-i18next";

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith("pt") ? "pt" : "en";

  const toggleLanguage = () => {
    const nextLang = currentLang === "pt" ? "en" : "pt";
    i18n.changeLanguage(nextLang);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={toggleLanguage}
      className="h-8 px-2 text-xs font-semibold gap-1.5 text-muted-foreground hover:text-foreground"
      aria-label={`Trocar idioma (atual: ${currentLang.toUpperCase()})`}
    >
      <Globe className="h-3.5 w-3.5" />
      <span>{currentLang.toUpperCase()}</span>
    </Button>
  );
}

// Lazy-loaded components that contain heavy Radix UI elements (only downloaded when needed)
const AuthModal = lazy(() => import("@/components/AuthModal").then(m => ({ default: m.AuthModal })));
const BecomeProducerModal = lazy(() => import("@/components/BecomeProducerModal").then(m => ({ default: m.BecomeProducerModal })));
const UserMenuActions = lazy(() => import("@/components/UserMenuActions").then(m => ({ default: m.UserMenuActions })));
const MobileUserMenu = lazy(() => import("@/components/UserMenuActions").then(m => ({ default: m.MobileUserMenu })));

// Prefetch do chunk JS da rota ao hover — elimina o download no momento do clique
const routePrefetchMap: Record<string, () => Promise<unknown>> = {
  "/eventos":   () => import("@/pages/Eventos"),
  "/revenda":   () => import("@/pages/Revenda"),
  "/produtores":() => import("@/pages/Produtores"),
  "/blog":      () => import("@/pages/Blog"),
  "/":          () => import("@/pages/Index"),
};
const prefetched = new Set<string>();
function prefetchRoute(href: string) {
  if (prefetched.has(href) || !routePrefetchMap[href]) return;
  prefetched.add(href);
  routePrefetchMap[href]();
}

export function Navbar() {
  const { t } = useTranslation("common");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<"login" | "register">("login");
  const [navSearch, setNavSearch] = useState("");
  const [producerModalOpen, setProducerModalOpen] = useState(false);
  const { user, profile, role, allRoles, switchRole, signOut } = useAuth();
  const { itemCount } = useCart();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const handleCreateEvent = () => {
    if (role === "producer") {
      navigate("/produtor/eventos/novo");
    } else {
      setProducerModalOpen(true);
    }
  };

  const isAdminOrProducer = location.pathname.startsWith("/admin") || location.pathname.startsWith("/producer") || location.pathname.startsWith("/produtor");
  const isLandingPage = location.pathname === "/";

  useEffect(() => {
    if (searchParams.get("login") === "true") {
      setAuthOpen(true);
      // Clean up query param
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams, navigate, location.pathname]);

  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handle, { passive: true });
    return () => window.removeEventListener("scroll", handle);
  }, []);

  const links = [
    { label: t("navbar.links.events"), href: "/eventos" },
    { label: t("navbar.links.resale"), href: "/revenda" },
    { label: t("navbar.links.producers"), href: "/produtores" },
    { label: t("navbar.links.blog"), href: "/blog" },
  ];

  const navLinks = user
    ? links.filter((link) => link.href !== "/blog")
    : links;

  const openLogin = () => { setAuthTab("login"); setAuthOpen(true); };
  const openRegister = () => { setAuthTab("register"); setAuthOpen(true); };

  const handleNavSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (navSearch.trim()) {
      navigate(`/eventos?q=${encodeURIComponent(navSearch.trim())}`);
      setNavSearch("");
    }
  };

  const roleLabels: Record<string, string> = {
    admin: t("navbar.roles.admin"),
    producer: t("navbar.roles.producer"),
    staff: t("navbar.roles.staff"),
    buyer: t("navbar.roles.buyer"),
  };

  const roleDashboardLinks: Record<string, string> = {
    admin: "/admin/relatorios",
    producer: "/produtor/dashboard",
    staff: "/staff",
    buyer: "/meus-ingressos",
  };


  const initials = profile?.full_name
    ? profile.full_name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  const firstName = profile?.full_name
    ? profile.full_name.trim().split(/\s+/)[0]
    : t("navbar.account.defaultName");

  const accountDisplayName = profile?.full_name
    ? firstName
    : t("navbar.account.myAccount");

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-200",
          scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border" : "bg-transparent"
        )}
      >
        <div className="container flex h-14 lg:h-16 items-center justify-between">
          <Link to="/" onMouseEnter={() => prefetchRoute("/")} className="shrink-0">
            <TicketHallLogo size="lg" className="hidden lg:block" />
            <TicketHallLogo size="md" className="lg:hidden" />
          </Link>

          {/* Desktop Navigation — visible at lg+ */}
          <nav className="hidden lg:flex items-center gap-6">
            {(scrolled || !isLandingPage) && !isAdminOrProducer && (
              <form onSubmit={handleNavSearch} className="relative">
                <SearchInput
                  type="text"
                  value={navSearch}
                  onChange={(e) => setNavSearch(e.target.value)}
                  placeholder={t("navbar.search.placeholder")}
                  className="h-8 w-[180px] text-sm rounded-full bg-muted/50 border-transparent focus:border-border focus:bg-background transition-all"
                />
              </form>
            )}
            {navLinks.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                onMouseEnter={() => prefetchRoute(l.href)}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions — visible at lg+ */}
          <div className="hidden lg:flex items-center gap-2">
            <LanguageSwitcher />
            <AnimatedThemeToggler />
            {user ? (
              <Suspense fallback={<div className="w-[120px] h-8 bg-muted/40 animate-pulse rounded-md" />}>
                <UserMenuActions
                  itemCount={itemCount}
                  accountDisplayName={accountDisplayName}
                  roleLabels={roleLabels}
                  role={role}
                  profile={profile}
                  initials={initials}
                  firstName={firstName}
                  user={user}
                  handleCreateEvent={handleCreateEvent}
                  allRoles={allRoles}
                  switchRole={switchRole}
                  roleDashboardLinks={roleDashboardLinks}
                  signOut={signOut}
                  navigate={navigate}
                />
              </Suspense>
            ) : (
              <>
                <Link 
                  to="/carrinho" 
                  className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={itemCount > 0 ? t("navbar.cart.ariaLabelWithCount", { count: itemCount }) : t("navbar.cart.ariaLabel")}
                >
                  <ShoppingBag className="h-5 w-5" />
                  {itemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full">
                      {itemCount > 99 ? "99+" : itemCount}
                    </span>
                  )}
                </Link>
                <Button variant="outline" size="sm" onClick={handleCreateEvent} className="gap-1.5">
                  <Plus className="h-3.5 w-3.5" />
                  {t("navbar.cta.createEvent")}
                </Button>
                <Button variant="ghost" size="sm" onClick={openLogin}>{t("navbar.auth.login")}</Button>
                <Button variant="default" size="sm" onClick={openRegister}>{t("navbar.auth.register")}</Button>
              </>
            )}
          </div>

          {/* Mobile/Tablet Header — visible below lg */}
          <div className="lg:hidden flex items-center gap-1">
            <LanguageSwitcher />
            <AnimatedThemeToggler />

            {/* Cart icon for logged-out users on mobile */}
            {!user && !isAdminOrProducer && (
              <Link
                to="/carrinho"
                className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={itemCount > 0 ? t("navbar.cart.ariaLabelWithCount", { count: itemCount }) : t("navbar.cart.ariaLabel")}
              >
                <ShoppingBag className="h-5 w-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full">
                    {itemCount > 99 ? "99+" : itemCount}
                  </span>
                )}
              </Link>
            )}

            {user && <NotificationBell />}

            {!user && !isAdminOrProducer && (
              <Button variant="default" size="sm" className="text-xs px-3 h-8" onClick={openLogin}>
                {t("navbar.auth.login")}
              </Button>
            )}

            {/* Hamburger menu — always visible on mobile */}
            <button className="lg:hidden text-foreground p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? t("navbar.mobileMenu.close") : t("navbar.mobileMenu.open")}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu — logged-out users */}
        {mobileOpen && !user && !isAdminOrProducer && (
          <div className="lg:hidden bg-background/95 backdrop-blur-xl border-b border-border animate-fade-in">
              <div className="container py-4 space-y-1">
              <Link
                to="/"
                className="flex items-center text-sm font-medium text-muted-foreground hover:text-foreground py-2.5 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {t("navbar.mobileMenu.home")}
              </Link>
              {navLinks.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  onMouseEnter={() => prefetchRoute(l.href)}
                  className="flex items-center text-sm font-medium text-muted-foreground hover:text-foreground py-2.5 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {l.label}
                </Link>
              ))}
              <div className="border-t border-border my-2" />
              <button
                className="flex items-center text-sm font-medium text-muted-foreground hover:text-foreground py-2.5 w-full transition-colors"
                onClick={() => { handleCreateEvent(); setMobileOpen(false); }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("navbar.cta.createEvent")}
              </button>
              <div className="flex gap-3 pt-3">
                <Button variant="ghost" size="sm" className="flex-1" onClick={() => { openLogin(); setMobileOpen(false); }}>{t("navbar.auth.login")}</Button>
                <Button variant="default" size="sm" className="flex-1" onClick={() => { openRegister(); setMobileOpen(false); }}>{t("navbar.auth.register")}</Button>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Menu — logged-in users (buyer / admin / producer) */}
        {mobileOpen && user && (
          <Suspense fallback={null}>
            <MobileUserMenu
              navLinks={navLinks}
              prefetchRoute={prefetchRoute}
              setMobileOpen={setMobileOpen}
              allRoles={allRoles}
              role={role}
              switchRole={switchRole}
              navigate={navigate}
              roleDashboardLinks={roleDashboardLinks}
              roleLabels={roleLabels}
              signOut={signOut}
            />
          </Suspense>
        )}
      </header>

      <Suspense fallback={null}>
        {authOpen && <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultTab={authTab} />}
        {producerModalOpen && <BecomeProducerModal open={producerModalOpen} onOpenChange={setProducerModalOpen} />}
      </Suspense>
    </>
  );
}